export interface LandmarkTeleportDestination {
  readonly id: string;
  readonly label: string;
  readonly tileId: string;
  readonly mapPosition?: Readonly<{
    x: number;
    z: number;
  }>;
  readonly arrivalPosition: Readonly<{
    x: number;
    z: number;
  }>;
  readonly facingYaw: number;
}

interface LandmarkReview {
  readonly distanceM: number;
  readonly bearingRadians: number;
}

interface LandmarkManifestEntry {
  readonly id: string;
  readonly label: string;
  readonly tileId: string;
  readonly position: Readonly<{
    x: number;
    z: number;
  }>;
  readonly review?: LandmarkReview;
}

const CORE_APPROACHES = new Map<
  string,
  Readonly<{
    arrivalPosition: Readonly<{ x: number; z: number }>;
    facingYaw: number;
  }>
>([
  [
    "vilnius-cathedral",
    {
      arrivalPosition: { x: 540, z: 267 },
      facingYaw: Math.PI / 2,
    },
  ],
  [
    "vilnius-cathedral-bell-tower",
    {
      arrivalPosition: { x: 564, z: 342 },
      facingYaw: Math.PI,
    },
  ],
  [
    "gediminas-castle",
    {
      arrivalPosition: { x: 840, z: 194 },
      facingYaw: Math.atan2(-19.363, -33.471),
    },
  ],
  [
    "vilnius-town-hall",
    {
      arrivalPosition: { x: 588, z: 1_065 },
      facingYaw: Math.atan2(3.644, 70.509),
    },
  ],
]);

const THREE_CROSSES_DESTINATION: LandmarkTeleportDestination = Object.freeze({
  id: "three-crosses",
  label: "Three Crosses",
  tileId: "4:0",
  mapPosition: Object.freeze({ x: 1_261.914, z: 161.8 }),
  arrivalPosition: Object.freeze({ x: 864, z: 176 }),
  facingYaw: Math.atan2(1_261.914 - 864, 161.8 - 176),
});

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function requireString(
  record: Readonly<Record<string, unknown>>,
  field: string,
  context: string,
): string {
  const value = record[field];
  if (typeof value !== "string" || value.trim() === "") {
    throw new Error(`${context}.${field} must be a non-empty string.`);
  }
  return value;
}

function requireFiniteNumber(
  record: Readonly<Record<string, unknown>>,
  field: string,
  context: string,
): number {
  const value = record[field];
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`${context}.${field} must be a finite number.`);
  }
  return value;
}

function parseEntry(value: unknown, index: number): LandmarkManifestEntry {
  const context = `landmarks[${index}]`;
  if (!isRecord(value)) {
    throw new Error(`${context} must be an object.`);
  }
  const positionValue = value.position;
  if (!isRecord(positionValue)) {
    throw new Error(`${context}.position must be an object.`);
  }
  const reviewValue = value.review;
  let review: LandmarkReview | undefined;
  if (reviewValue !== undefined) {
    if (!isRecord(reviewValue)) {
      throw new Error(`${context}.review must be an object.`);
    }
    review = {
      distanceM: requireFiniteNumber(
        reviewValue,
        "distanceM",
        `${context}.review`,
      ),
      bearingRadians: requireFiniteNumber(
        reviewValue,
        "bearingRadians",
        `${context}.review`,
      ),
    };
    if (review.distanceM < 1) {
      throw new Error(`${context}.review.distanceM must be at least one metre.`);
    }
  }
  return {
    id: requireString(value, "id", context),
    label: requireString(value, "label", context),
    tileId: requireString(value, "tileId", context),
    position: {
      x: requireFiniteNumber(positionValue, "x", `${context}.position`),
      z: requireFiniteNumber(positionValue, "z", `${context}.position`),
    },
    ...(review === undefined ? {} : { review }),
  };
}

function destinationFor(
  landmark: Readonly<LandmarkManifestEntry>,
): LandmarkTeleportDestination {
  const coreApproach = CORE_APPROACHES.get(landmark.id);
  if (coreApproach !== undefined) {
    return {
      id: landmark.id,
      label: landmark.label,
      tileId: landmark.tileId,
      mapPosition: { x: landmark.position.x, z: landmark.position.z },
      arrivalPosition: { ...coreApproach.arrivalPosition },
      facingYaw: coreApproach.facingYaw,
    };
  }
  if (landmark.review === undefined) {
    throw new Error(
      `Landmark ${landmark.id} has no reviewed teleport approach.`,
    );
  }
  const headingX = Math.sin(landmark.review.bearingRadians);
  const headingZ = Math.cos(landmark.review.bearingRadians);
  return {
    id: landmark.id,
    label: landmark.label,
    tileId: landmark.tileId,
    mapPosition: { x: landmark.position.x, z: landmark.position.z },
    arrivalPosition: {
      x: landmark.position.x - headingX * landmark.review.distanceM,
      z: landmark.position.z - headingZ * landmark.review.distanceM,
    },
    facingYaw: landmark.review.bearingRadians,
  };
}

export function parseLandmarkTeleportDestinations(
  value: unknown,
): readonly LandmarkTeleportDestination[] {
  if (!isRecord(value) || value.schemaVersion !== 1) {
    throw new Error("Landmark manifest must use schema version 1.");
  }
  if (!Array.isArray(value.landmarks)) {
    throw new Error("Landmark manifest landmarks must be an array.");
  }
  const entries = value.landmarks.map(parseEntry);
  const destinations = entries.map(destinationFor);
  if (!entries.some((entry) => entry.id === "three-crosses-monument")) {
    // M4 used a lightweight runtime silhouette outside the authored manifest.
    // Keep its reviewed transfer as a compatibility fallback for older
    // manifests, but do not duplicate the source-backed Blender replacement.
    destinations.push(THREE_CROSSES_DESTINATION);
  }
  const ids = new Set<string>();
  for (const destination of destinations) {
    if (ids.has(destination.id)) {
      throw new Error(`Duplicate landmark destination ${destination.id}.`);
    }
    ids.add(destination.id);
  }
  return destinations.sort((left, right) =>
    left.label.localeCompare(right.label, "lt")
  );
}

export async function loadLandmarkTeleportDestinations(
  manifestUrl: string,
  fetcher: typeof fetch = fetch,
): Promise<readonly LandmarkTeleportDestination[]> {
  const response = await fetcher(manifestUrl);
  if (!response.ok) {
    throw new Error(
      `Could not load landmark teleport destinations (${response.status}).`,
    );
  }
  return parseLandmarkTeleportDestinations(await response.json());
}
